import { $http } from "@/utils/https";
import type { ActionContext } from "vuex";
import type { RootState } from "@/store";

type Webinar = {
  id: number,
  status: boolean,
  position: number,
  old_price: string,
  tag: string,
  title: string,
  sub_title: string,
  banner: null | string,
  user_progress: null | number
}

type Lesson = {
  id: number,
  preview: string,
  video: string,
  material: string,
  title: string,
  subTitle: string,
  userStatus: string
}


interface State {
  webinarList: Webinar[],
  webinar: Webinar | null,
  loadingWebinarList: boolean
  content: any;
  lesson: Lesson | null,
}

const state: State = {
  content: null,
  webinarList: [],
  webinar: null,
  lesson: null,
  loadingWebinarList: false
};

const getters = {
  content: (state: State) => state.content,
  webinarList: (state: State) => state.webinarList,
  webinar: (state: State) => state.webinar,
  lesson: (state: State) => state.lesson,
  loadingWebinarList: (state: State) => state.loadingWebinarList,
};

type CosmoBaseFormPayload = {
  name: string;
  last_name: string;
  phone: string;
}

type SpecialCosmoBaseFormPayload = {
  name: string;
  last_name: string;
  phone: string;
  comment: string;
}

const actions = {
  FETCH_COSMOBASE_PAGE: async ({ commit }: ActionContext<State, RootState>) => {
    try {
      const response = await $http.get("v1/pages/cosmo-base");
      commit("SET_COSMOBASE_PAGE", response.data.data);
      return response.data.data;
    } catch (e) {
      throw e;
    }
  },
  SEND_COSMOBASE_FORM: async ({ commit }: ActionContext<State, RootState>, payload: CosmoBaseFormPayload) => {
    try {
      const response = await $http.post("v1/store_cosmo_base_form", payload);
      return response.data;
    } catch (e) {
      throw e;
    }
  },
  SEND_SPECIAL_COSMOBASE_FORM: async (
    { commit }: ActionContext<State, RootState>,
    payload: SpecialCosmoBaseFormPayload
  ) => {
    try {
      const response = await $http.post("v1/store_cosmo_base_special_form", payload);
      return response.data;
    } catch (e) {
      throw e;
    }
  },
  SEND_SECOND_COSMOBASE_FORM: async (
    { commit }: ActionContext<State, RootState>,
    payload: SpecialCosmoBaseFormPayload
  ) => {
    try {
      const response = await $http.post("v1/store_webinar_page_form", payload);
      return response.data;
    } catch (e) {
      throw e;
    }
  },
  GET_WEBINARS: async ({ commit, state }: ActionContext<State, RootState>) => {
    state.loadingWebinarList = true;
    try {
      commit("SET_WEBINARS", null);
      const response = await $http.get<{ data: Array<Webinar> }>("v1/webinars");
      commit("SET_WEBINARS", response.data.data);
    } catch (e) {
      throw e;
    } finally {
      state.loadingWebinarList = false;
    }
  },
  GET_WEBINARS_BY_ID: async ({ commit, state }: ActionContext<State, RootState>, id: number) => {
    state.loadingWebinarList = true;
    try {
      commit("SET_WEBINAR", null);
      const response = await $http.get<{ data: Array<Webinar> }>(`v1/webinars/${id}`);
      commit("SET_WEBINAR", response.data.data);
    } catch (e) {
      throw e;
    } finally {
      state.loadingWebinarList = false;
    }
  },
  GET_LESSON_BY_ID: async ({ commit, state }: ActionContext<State, RootState>, id: number) => {
    state.loadingWebinarList = true;
    try {
      commit("SET_LESSON", null);
      const response = await $http.get<{ data: Lesson }>(`v1/webinars/lesson/${id}`);
      commit("SET_LESSON", response.data.data);
    } catch (e) {
      throw e;
    } finally {
      state.loadingWebinarList = false;
    }
  },
  SEND_COMPLETE_LESSON_VIDEO: async ({ commit, state }: ActionContext<State, RootState>, id: number) => {
    state.loadingWebinarList = true;
    try {
      const response = await $http.post<{ data: Lesson }>(`v1/webinars/complete-lesson-video?lesson_video_id=${id}`);
      // @ts-ignore
      return response.data.userProgress;
    } catch (e) {
      throw e;
    } finally {
      state.loadingWebinarList = false;
    }
  },
  GENERATE_CERTIFICATE: async ({ commit, state }: ActionContext<State, RootState>, id: number) => {
    state.loadingWebinarList = true;
    try {
      const response = await $http.post<{ url: string }>(`v1/webinars/generate_certificate/${id}`);
      return response.data.url;
    } catch (e) {
      throw e;
    } finally {
      state.loadingWebinarList = false;
    }
  },
};

const mutations = {
  SET_WEBINARS(state: State, list: Array<Webinar>) {
    state.webinarList = list;
  },
  SET_LESSON(state: State, list: Lesson) {
    state.lesson = list;
  },
  SET_WEBINAR(state: State, payload: Webinar) {
    state.webinar = payload;
  },
  SET_COSMOBASE_PAGE(state: State, products: any) {
    state.content = products;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
